import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { authAPI } from "../Services/api";
import { paymentsAPI } from "../Services/Dashboard/PaymentsApi";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tokenChecked, setTokenChecked] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!tokenChecked) {
      checkAuth();
    }
  }, [tokenChecked]);

  // Listen for storage changes to sync auth state across tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "token" || e.key === "user") {
        if (e.key === "token" && !e.newValue) {
          // Token was removed (logout in another tab)
          setUser(null);
        } else if (e.key === "user" && e.newValue) {
          // User data was updated in another tab
          try {
            setUser(JSON.parse(e.newValue));
          } catch (error) {
          }
        } else if (e.key === "token" && e.newValue) {
          // Token was added (login in another tab) - refresh auth
          checkAuth();
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem("token");
      const storedUser = localStorage.getItem("user");

      if (!token) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        setUser(null);
        setLoading(false);
        setTokenChecked(true);
        return;
      }

      if (storedUser) {
        // Set user from localStorage first for faster UI response
        try {
          setUser(JSON.parse(storedUser));
        } catch (e) {
          localStorage.removeItem("user");
        }
      }

      // Then verify with server
      const result = await authAPI.getCurrentUser();
      if (result.success && result.data.user) {
        setUser(result.data.user);
        localStorage.setItem("user", JSON.stringify(result.data.user));
      } else {
        // Token is invalid - clear everything
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        setUser(null);
      }
    } catch (error) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setUser(null);
    } finally {
      setLoading(false);
      setTokenChecked(true);
    }
  };

  // Force refresh token check - useful after certain operations
  const refreshAuth = () => {
    setTokenChecked(false); // This will trigger the useEffect to run checkAuth again
  };

  const signup = async (username, email, contact, password) => {
    try {
      const result = await authAPI.signup({
        username,
        email,
        contact,
        password,
      });
      if (result.success) {
        // Don't navigate - let component handle the UI change
        return {
          success: true,
          data: result.data,
        };
      }
      return {
        success: false,
        error: result.error || "Signup failed",
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || "Signup failed",
      };
    }
  };

  const handlePostLoginRedirect = async () => {
    const params = new URLSearchParams(window.location.search);
    const plan = params.get("plan");
    if (plan && ["starter", "professional"].includes(plan)) {
      const res = await paymentsAPI.createCheckoutSession(plan);
      if (res.success && res.data.url) {
        window.location.href = res.data.url;
        return true;
      }
    }
    return false;
  };

  const login = async (email, password) => {
    try {
      setLoading(true);
      localStorage.removeItem("token");
      localStorage.removeItem("user");

      const result = await authAPI.login({ email, password });
      if (result.success) {
        if (result.data.user) {
          setUser(result.data.user);
          const redirected = await handlePostLoginRedirect();
          if (!redirected) navigate("/dashboard");
        } else {
          try {
            const userResult = await authAPI.getCurrentUser();
            if (userResult.success) {
              setUser(userResult.data.user);
              localStorage.setItem(
                "user",
                JSON.stringify(userResult.data.user)
              );
              const redirected = await handlePostLoginRedirect();
              if (!redirected) navigate("/dashboard");
            }
          } catch (userError) {
            return {
              success: false,
              error: "Failed to get user data after login",
            };
          }
        }
        return { success: true };
      }
      return {
        success: false,
        error: result.error || "Login failed",
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || "Login failed",
      };
    } finally {
      setLoading(false);
      setTokenChecked(true);
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    navigate("/login");
  };

  // Update user data in context and localStorage
  const updateUser = (updatedUserData) => {
    const updatedUser = { ...user, ...updatedUserData };
    setUser(updatedUser);
    localStorage.setItem("user", JSON.stringify(updatedUser));
    return updatedUser;
  };

  // Update subscription specifically
  const updateSubscription = (subscription) => {
    return updateUser({ subscription });
  };

  const getSubscriptionTier = () => user?.subscription || 'free';

  const hasAccess = () => true;

  const isAdmin = () => user?.role === "admin";
  const isSuperAdmin = () => user?.role === "superadmin";
  const isClient = () => user?.role === "client";
  const isEmployee = () => user?.role === "employee";

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        signup,
        updateUser,
        updateSubscription,
        getSubscriptionTier,
        hasAccess,
        isAdmin,
        isSuperAdmin,
        isClient,
        isEmployee,
        refreshAuth
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
